import { Clock, Plus } from "lucide-react";
import { format, addDays } from "date-fns";

export function WeeklyPlan({
  mealTypes,
  structuredMealPlan,
  handleOpenModal,
  currentDayIndex,
  setCurrentDayIndex,
  weekStart,
  isPastWeek,
}) {
  const renderMealBadges = (entry) => {
    const badges = [];

    if (entry.isLeftover) {
      badges.push(
        <span
          key="leftover"
          className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium"
        >
          Leftover
        </span>,
      );
    }

    if (entry.ingredientMultiplier === 2) {
      badges.push(
        <span
          key="multiplier"
          className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium"
        >
          2x Portions
        </span>,
      );
    }

    return badges;
  };

  const getDayTotalMins = (dayIndex) =>
    mealTypes.reduce((sum, meal) => {
      const entries = structuredMealPlan[dayIndex]?.[meal] || [];
      return sum + entries.reduce((s, e) => s + (e.timeMins || 30), 0);
    }, 0);

  const formatTotalTime = (mins) => {
    if (mins === 0) return null;
    if (mins < 60) return `~${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
  };

  return (
    <div className="min-w-max">
      {/* Days Header */}
      <div className="grid grid-cols-[140px_repeat(7,minmax(200px,1fr))] gap-3 mb-3">
        <div />
        {[...Array(7)].map((_, i) => {
          const day = addDays(weekStart, i);
          const totalTime = formatTotalTime(getDayTotalMins(i));
          return (
            <div key={i} className="text-center">
              <div className="text-sm font-medium text-gray-900">
                {format(day, "EEE")}
              </div>
              <div className="text-xs text-gray-500">
                {format(day, "MMM d")}
              </div>
              {totalTime && (
                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span>{totalTime}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Meals Grid */}
      {mealTypes.map((meal) => (
        <div
          key={meal}
          className="grid grid-cols-[140px_repeat(7,minmax(200px,1fr))] gap-3 mb-3"
        >
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700">{meal}</span>
          </div>

          {[...Array(7)].map((_, dayIndex) => {
            const mealData = structuredMealPlan[dayIndex]?.[meal] || [];

            return (
              <div
                key={dayIndex}
                onClick={isPastWeek ? undefined : () => handleOpenModal(dayIndex, meal)}
                className={`bg-white border border-gray-200 rounded-lg p-4 min-h-[80px] transition-colors ${
                  isPastWeek ? "opacity-60" : "hover:border-gray-300 cursor-pointer group"
                }`}
              >
                {mealData.length > 0 ? (
                  <div className="max-h-24 overflow-y-auto space-y-2">
                    {mealData.map((entry) => {
                      const badges = renderMealBadges(entry);
                      return (
                        <div key={entry.entryId} className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {entry.mealName}
                            </p>
                            {badges.length > 0 && (
                              <div className="flex gap-1 mt-0.5 flex-wrap">
                                {badges}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground mt-0.5">
                            <Clock className="w-3 h-3" />
                            <span>{entry.timeMins || 30}m</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  !isPastWeek && (
                    <div className="flex items-center justify-center h-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
